import { useEffect } from 'react';
import { useDashboardStore } from '../stores/dashboardStore';
import { StatCard } from '../components/ui/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import {
  Users,
  Activity,
  PlayCircle,
  Brain,
  DollarSign,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

export function DashboardPage() {
  const { stats, topFailures, isLoading, fetchStats, fetchTopFailures } = useDashboardStore();

  useEffect(() => {
    fetchStats();
    fetchTopFailures();
  }, [fetchStats, fetchTopFailures]);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('zh-CN').format(num);
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(num);
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${(seconds / 60).toFixed(1)}m`;
  };

  if (isLoading || !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">Dashboard</h1>
        <p className="text-zinc-500 mt-1">实时监控平台运营数据</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="今日新增用户"
          value={formatNumber(stats.newUsersToday)}
          change={12.5}
          icon={Users}
          iconColor="text-blue-600"
        />
        <StatCard
          title="今日活跃用户"
          value={formatNumber(stats.activeUsersToday)}
          change={8.3}
          icon={Activity}
          iconColor="text-accent-600"
        />
        <StatCard
          title="今日运行次数"
          value={formatNumber(stats.runsToday)}
          change={-5.2}
          icon={PlayCircle}
          iconColor="text-success-600"
        />
        <StatCard
          title="今日 AI 调用"
          value={formatNumber(stats.aiCallsToday)}
          change={15.7}
          icon={Brain}
          iconColor="text-purple-600"
        />
      </div>

      {/* Second Row Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="今日收入"
          value={formatCurrency(stats.revenueToday)}
          change={23.1}
          icon={DollarSign}
          iconColor="text-amber-600"
        />
        <StatCard
          title="Token 使用量"
          value={formatNumber(stats.tokensToday)}
          icon={Brain}
          iconColor="text-purple-600"
        />
        <StatCard
          title="AI 成本"
          value={formatCurrency(stats.aiCostToday)}
          icon={DollarSign}
          iconColor="text-amber-600"
        />
        <StatCard
          title="平均执行时间"
          value={formatDuration(stats.avgExecutionTime)}
          change={-10.5}
          icon={Clock}
          iconColor="text-blue-600"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle>用户增长趋势</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.userTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => new Date(value).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}
                    stroke="#71717a"
                    fontSize={12}
                  />
                  <YAxis stroke="#71717a" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e4e4e7',
                      borderRadius: '8px',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#4f46e5"
                    strokeWidth={2}
                    dot={{ fill: '#4f46e5', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* AI Cost Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle>AI 成本趋势</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.aiCostTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => new Date(value).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}
                    stroke="#71717a"
                    fontSize={12}
                  />
                  <YAxis
                    stroke="#71717a"
                    fontSize={12}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e4e4e7',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [`$${value.toFixed(2)}`, '成本']}
                  />
                  <Bar dataKey="value" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Stats & Top Failures */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Workflow Stats */}
        <Card>
          <CardHeader>
            <CardTitle>工作流运行统计</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-success-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-success-600" />
                  <span className="text-sm font-medium text-zinc-700">成功率</span>
                </div>
                <span className="text-lg font-bold text-success-600">
                  {stats.successRate.toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-error-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <XCircle className="w-5 h-5 text-error-600" />
                  <span className="text-sm font-medium text-zinc-700">失败率</span>
                </div>
                <span className="text-lg font-bold text-error-600">
                  {stats.failureRate.toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-accent-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-accent-600" />
                  <span className="text-sm font-medium text-zinc-700">总运行次数</span>
                </div>
                <span className="text-lg font-bold text-accent-600">
                  {formatNumber(stats.totalRunsToday)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Failures */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>异常监控 TOP 10</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Workflow Failures */}
              <div>
                <h4 className="text-sm font-medium text-zinc-500 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  失败流程
                </h4>
                <div className="space-y-2">
                  {topFailures?.workflowFailures.slice(0, 5).map((failure, index) => (
                    <div
                      key={failure.workflowId}
                      className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 flex items-center justify-center rounded-full bg-zinc-200 text-xs font-medium text-zinc-600">
                          {index + 1}
                        </span>
                        <span className="text-sm font-medium text-zinc-700">
                          {failure.workflowName}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-zinc-500">
                          {failure.failureCount} 次失败
                        </span>
                        <span className="text-sm font-medium text-error-600">
                          {failure.failureRate.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ))}
                  {(!topFailures?.workflowFailures || topFailures.workflowFailures.length === 0) && (
                    <p className="text-sm text-zinc-400 text-center py-4">暂无数据</p>
                  )}
                </div>
              </div>

              {/* AI Failures */}
              {topFailures?.aiFailures && topFailures.aiFailures.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-zinc-500 mb-3 flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    AI 调用失败
                  </h4>
                  <div className="space-y-2">
                    {topFailures.aiFailures.slice(0, 3).map((failure, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
                      >
                        <span className="text-sm font-medium text-zinc-700">
                          {failure.errorType}
                        </span>
                        <span className="text-sm font-medium text-error-600">
                          {failure.count} 次
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
